import { useState, useEffect } from 'react';
import { useAuth } from '../../features/auth';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface DiscordWidgetData {
  id: string;
  name: string;
  instant_invite: string;
  presence_count: number;
  channels: Array<{
    id: string;
    name: string;
    position: number;
  }>;
}

const DISCORD_WIDGET_ID = '1327760211116687422';
const DISCORD_WIDGET_API = `https://discord.com/api/guilds/${DISCORD_WIDGET_ID}/widget.json`;


const CommunityPage = () => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [discordData, setDiscordData] = useState<DiscordWidgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDiscordStats = async () => {
      try {
        setLoading(true);
        const response = await fetch(DISCORD_WIDGET_API, {
          mode: 'cors',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(
            response.status === 403 
              ? t('community.discord.cors_error')
              : t('community.discord.fetch_error')
          );
        }
        
        const data = await response.json();
        setDiscordData(data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar dados do Discord:', err);
        setError(t('community.discord.generic_error'));
        if (import.meta.env.DEV) {
          setDiscordData({
            id: DISCORD_WIDGET_ID,
            name: 'DevPanc Community',
            instant_invite: 'https://discord.gg/your-invite-link',
            presence_count: 0,
            channels: []
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDiscordStats();
  }, [t]);

  return (
    <motion.div 
      className="min-h-screen flex flex-col bg-base-300 home-page overflow-x-hidden relative pt-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Background circuit effect */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5 pointer-events-none"></div>
      
      {/* Animated circuit lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="circuit-line w-full h-1 bg-primary/30 absolute top-[20%] left-0 animate-pulse"></div>
        <div className="circuit-line w-1 h-full bg-primary/30 absolute top-0 left-[30%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
        <div className="circuit-line w-full h-1 bg-secondary/30 absolute top-[70%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="circuit-line w-1 h-full bg-secondary/30 absolute top-0 left-[80%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center justify-center py-8 sm:py-16">
          <div className="text-center mb-10">
            <div className="glitch-container inline-block mb-2">
              <h1 
                data-text="COMUNIDADE"
                className="text-3xl sm:text-5xl font-bold text-primary font-tech tracking-wider"
              >
                COMUNIDADE
              </h1>
            </div>
            <div className="h-1 w-20 bg-primary mx-auto mt-3"></div>
            <p className="text-base sm:text-lg uppercase tracking-wider text-base-content/90 font-tech-secondary mt-4 neon-text">
              APRENDA • COMPARTILHE • EVOLUA
            </p>
          </div>

          {error && (
            <div role="alert" className="alert alert-error shadow-lg mb-8 bg-base-100/90 border border-error/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-error flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-tech">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center mb-8">
              <div className="relative">
                <span className="loading loading-spinner loading-lg text-primary"></span>
                <div className="absolute inset-0 animate-pulse-glow opacity-50"></div>
              </div>
            </div>
          ) : discordData && (
            <div className="stats shadow mb-8 bg-base-100/90 border border-primary/20 backdrop-blur-sm hover:shadow-primary/20">
              <div className="stat">
                <div className="stat-title font-tech text-base-content/90">Membros Online</div>
                <div className="stat-value text-primary font-tech-secondary neon-text">
                  {discordData.presence_count}
                </div>
                <div className="stat-desc text-base-content/80">Na comunidade do Discord</div>
                <div className="mt-2 text-xs text-primary">{discordData.name}</div>
              </div>
            </div>
          )}

          <div className="flex flex-col items-center gap-8 w-full max-w-4xl">
            <div className="w-full mb-4">
              <a
                href={discordData?.instant_invite || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-primary font-tech w-full max-w-md mx-auto flex items-center justify-center gap-3 py-6 
                  shadow-lg hover:shadow-primary/20 transition-all duration-300 neon-glow
                  ${!discordData?.instant_invite ? 'opacity-50' : ''}`}
                onClick={(e) => {
                  if (!discordData?.instant_invite) {
                    e.preventDefault();
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 127.14 96.36" fill="currentColor">
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
                {loading ? 'Carregando...' : 'Entrar no Servidor Discord'}
              </a>
            </div>

            <div className="w-full h-[500px] card bg-base-100/90 shadow-xl border border-primary/20 backdrop-blur-sm overflow-hidden hover:shadow-primary/20">
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="circuit-line w-full h-px bg-primary/20 absolute top-[15%] left-0 animate-pulse"></div>
                <div className="circuit-line w-px h-full bg-secondary/20 absolute top-0 right-[15%] animate-pulse" style={{animationDelay: '0.9s'}}></div>
              </div>
              
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8 relative z-10">
                  <div className="relative">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                    <div className="absolute inset-0 animate-pulse-glow opacity-50"></div>
                  </div>
                  <p className="text-base-content/90 font-tech">Carregando informações do servidor...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center relative z-10">
                  <div className="text-error text-6xl mb-4">!</div>
                  <p className="text-base-content/90 font-tech">Não foi possível carregar o widget do Discord.</p>
                  <p className="text-base-content/70">Por favor, tente novamente mais tarde.</p>
                  <button onClick={() => window.location.reload()} className="btn btn-outline btn-primary btn-sm mt-4 neon-glow">
                    Tentar Novamente
                  </button>
                </div>
              ) : discordData?.channels?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-6 p-8 text-center relative z-10">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-primary/50 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-base-content/90 font-tech">
                    O widget do Discord está ativo, mas nenhum canal está configurado para ser exibido.
                  </p>
                  <div className="card bg-base-300/70 p-5 border border-info/10">
                    <p className="mb-3 font-tech text-sm">Canais sugeridos para exibir:</p>
                    <ul className="list-none p-0 space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <span className="text-info">📢</span> anúncios
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <span className="text-info">👋</span> boas-vindas
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <span className="text-info">💬</span> geral
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <iframe 
                  src={`https://discord.com/widget?id=${DISCORD_WIDGET_ID}&theme=dark`}
                  className="w-full h-full rounded-xl relative z-10"
                  style={{ border: 'none' }}
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                  title="Discord Widget"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    <br></br>
    </motion.div>
  );
};

export default CommunityPage;
